import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Bell, Loader2, Send, ShieldAlert, Save } from "lucide-react";

const FREQS = [
  ["daily", "Daily digest", "One email each day summarizing all new replies"],
  ["weekly", "Weekly digest", "One email a week with the week's new replies"],
  ["off", "Paused", "Stop sending digests for now"],
];

export default function CircleTender() {
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const [state, setState] = useState("loading"); // loading | ok | denied
  const [recipients, setRecipients] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [lastSent, setLastSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!key) { setState("denied"); return; }
    api.get(`/notifications/tender?key=${encodeURIComponent(key)}`)
      .then((r) => {
        setRecipients((r.data.recipients || []).join("\n"));
        setFrequency(r.data.frequency || "daily");
        setLastSent(r.data.last_sent_at || null);
        setState("ok");
      })
      .catch(() => setState("denied"));
  }, [key]);

  const parseRecipients = () => recipients.split(/[\n,]/).map((e) => e.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true);
    try {
      const submitted = parseRecipients();
      const r = await api.put(`/notifications/tender?key=${encodeURIComponent(key)}`,
        { recipients: submitted, frequency });
      const saved = r.data.recipients || [];
      setRecipients(saved.join("\n"));
      setFrequency(r.data.frequency || "daily");
      if (saved.length < submitted.length)
        toast.warning(`Saved. ${submitted.length - saved.length} invalid or duplicate email(s) were removed.`);
      else
        toast.success("Notification settings saved");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post(`/notifications/tender/send-now?key=${encodeURIComponent(key)}`);
      if (r.data.sent) toast.success(`Digest sent — ${r.data.count} new ${r.data.count === 1 ? "reply" : "replies"} to ${r.data.recipients} recipient(s)`);
      else toast.info(r.data.reason === "no new replies" ? "No new replies to send right now." : r.data.reason === "no recipients" ? "Add at least one recipient email first." : `Not sent: ${r.data.reason}`);
      // refresh last sent
      api.get(`/notifications/tender?key=${encodeURIComponent(key)}`).then((rr) => setLastSent(rr.data.last_sent_at || null)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const field = "w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm";

  if (state === "loading")
    return <main className="bg-onyx min-h-screen pt-32 text-center text-bone/50" data-testid="tender-loading"><Loader2 className="animate-spin inline" /> Opening the circle...</main>;

  if (state === "denied")
    return (
      <main className="bg-onyx min-h-screen pt-32 pb-20 px-5" data-testid="tender-denied">
        <div className="max-w-md mx-auto bg-surface border border-white/10 p-10 text-center">
          <ShieldAlert size={40} className="text-gold/70 mx-auto mb-5" />
          <h1 className="font-serif text-2xl text-bone mb-3">Invalid or expired link</h1>
          <p className="text-bone/55 text-sm">This management link is not valid. Ask the site owner for a current link — the previous one may have been regenerated.</p>
        </div>
      </main>
    );

  return (
    <main className="bg-onyx min-h-screen pt-24 pb-24 px-5" data-testid="tender-page">
      <div className="max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2"><Bell size={15} /> Reasoning Circle · Notifications</p>
        <h1 className="font-serif text-4xl md:text-5xl text-bone mb-3">Tend the Circle</h1>
        <p className="text-bone/55 mb-10">Manage who receives the forum digest and how often — no login required. Anyone with this link can update these settings.</p>

        <div className="bg-surface border border-white/10 p-8 space-y-8">
          <div>
            <label className="text-xs uppercase tracking-widest text-bone/60 mb-3 block">Digest frequency</label>
            <div className="space-y-2">
              {FREQS.map(([val, label, desc]) => (
                <label key={val} data-testid={`tender-freq-${val}`}
                  className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors ${frequency === val ? "border-gold/60 bg-gold/5" : "border-white/10 hover:border-white/25"}`}>
                  <input type="radio" name="freq" value={val} checked={frequency === val} onChange={() => setFrequency(val)} className="accent-gold mt-0.5" />
                  <span>
                    <span className="text-bone text-sm font-medium block">{label}</span>
                    <span className="text-bone/50 text-xs">{desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-bone/60 mb-1.5 block">Recipient emails</label>
            <p className="text-bone/40 text-xs mb-2">One per line (or comma-separated). These addresses receive the digest.</p>
            <textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={5}
              placeholder={"tender@example.com\nmoderator@example.com"} className={field} data-testid="tender-recipients" />
          </div>

          {lastSent && <p className="text-bone/40 text-xs">Last digest sent: {new Date(lastSent).toLocaleString()}</p>}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button onClick={save} disabled={busy} data-testid="tender-save"
              className="inline-flex items-center gap-2 bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold2 disabled:opacity-50 transition-colors">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={15} />} Save settings
            </button>
            <button onClick={sendNow} disabled={sending} data-testid="tender-send-now"
              className="inline-flex items-center gap-2 border border-white/15 text-bone/80 px-5 py-2.5 text-sm hover:border-gold/50 hover:text-gold disabled:opacity-50 transition-colors">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Send digest now
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
